import { useCallback, useEffect, useRef } from "react";
import { loadDriverDeliveries } from "../lib/driverPortal.js";
import {
  buildDriverPortalLiveSyncFingerprint,
  DRIVER_PORTAL_LIVE_SYNC_POLL_INTERVAL_MS,
} from "../utils/driverPortalLiveSync.js";
import { subscribePlanningStateChange } from "../utils/planningCrossViewSync.js";

function normalizeInlineText(value) {
  return typeof value === "string" ? value.trim() : "";
}

export default function useDriverPortalLiveSync({
  currentPayload = null,
  dispatchDate = "",
  dispatchPeriod = "",
  enabled = true,
  onRemoteChange,
  onUnavailable = null,
  previewDriverUserId = "",
  recoverWhenAvailable = false,
}) {
  const pendingRefreshRef = useRef(false);
  const recoveryRefreshRequestedRef = useRef(false);
  const localFingerprint = buildDriverPortalLiveSyncFingerprint(currentPayload);

  const runRefresh = useCallback(() => {
    if (!enabled || typeof onRemoteChange !== "function") {
      return;
    }

    pendingRefreshRef.current = false;
    void onRemoteChange();
  }, [enabled, onRemoteChange]);

  const probeRemoteDriverPortalFingerprint = useCallback(async () => {
    const safeDispatchDate = normalizeInlineText(dispatchDate);
    if (
      !enabled
      || !safeDispatchDate
      || (typeof document !== "undefined" && document.visibilityState === "hidden")
    ) {
      return;
    }

    try {
      const payload = await loadDriverDeliveries({
        dispatchDate: safeDispatchDate,
        dispatchPeriod,
        previewDriverUserId,
      });
      const remoteFingerprint = buildDriverPortalLiveSyncFingerprint(payload);
      const currentFingerprint = buildDriverPortalLiveSyncFingerprint(currentPayload);

      if (recoverWhenAvailable && !recoveryRefreshRequestedRef.current) {
        recoveryRefreshRequestedRef.current = true;
        runRefresh();
        return;
      }

      if (
        currentFingerprint
        && remoteFingerprint
        && remoteFingerprint !== currentFingerprint
      ) {
        runRefresh();
      }
    } catch (error) {
      if (Number(error?.status) === 404 && typeof onUnavailable === "function") {
        onUnavailable(error);
      }
      // Les autres erreurs de sonde restent transitoires; le prochain passage réessaiera.
    }
  }, [currentPayload, dispatchDate, dispatchPeriod, enabled, onUnavailable, previewDriverUserId, recoverWhenAvailable, runRefresh]);

  useEffect(() => {
    if (!recoverWhenAvailable) {
      recoveryRefreshRequestedRef.current = false;
    }
  }, [recoverWhenAvailable]);

  useEffect(() => {
    recoveryRefreshRequestedRef.current = false;
  }, [dispatchDate, dispatchPeriod, previewDriverUserId]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    return subscribePlanningStateChange((detail) => {
      if (detail?.source !== "dispatch" && detail?.source !== "data-manager") {
        return;
      }

      runRefresh();
    });
  }, [enabled, runRefresh]);

  useEffect(() => {
    const safeDispatchDate = normalizeInlineText(dispatchDate);
    if (!enabled || !safeDispatchDate || typeof window === "undefined") {
      return undefined;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void probeRemoteDriverPortalFingerprint();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    const intervalId = window.setInterval(() => {
      void probeRemoteDriverPortalFingerprint();
    }, DRIVER_PORTAL_LIVE_SYNC_POLL_INTERVAL_MS);

    void probeRemoteDriverPortalFingerprint();

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [dispatchDate, dispatchPeriod, enabled, probeRemoteDriverPortalFingerprint]);

  return {
    localFingerprint,
  };
}
